import { Component, inject, viewChild } from '@angular/core';
import {
  AddEvent,
  CancelEvent,
  DataBindingDirective,
  EditEvent,
  GridComponent,
  KENDO_GRID,
  SaveEvent
} from "@progress/kendo-angular-grid";
import { GsmCreateModel, GsmFullModel, GsmFullModelWithStatus } from "../../../models/gsm.model";
import { KENDO_GRID_PAGE_SIZES } from "../../../constants/api.constant";
import { SVGIcon, pencilIcon } from "@progress/kendo-svg-icons";
import { ApiService } from "../../../services/api.service";
import { AlertService } from "../../../shared/alert/alert.service";
import { SpinnerService } from "../../../shared/spinner/spinner.service";
import { AlertEnum } from "../../../shared/alert/alert.enum";
import { InputsModule, TextBoxComponent } from "@progress/kendo-angular-inputs";
import { process } from "@progress/kendo-data-query";
import { CommonModule, DatePipe } from "@angular/common";
import { KENDO_CHIP } from "@progress/kendo-angular-buttons";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { ProfileService } from "../../../services/profile.service";
import { KENDO_DROPDOWNLIST } from "@progress/kendo-angular-dropdowns";
import { GsmType, StatusTypes } from "../../../enums/general-system-message.enum";
import { KENDO_DATEINPUTS } from "@progress/kendo-angular-dateinputs";
import { KENDO_PAGER } from "@progress/kendo-angular-pager";
import { LabelModule } from "@progress/kendo-angular-label";
import { NotificationService } from "../../../services/notification.service";
import { dateRangeValidator } from '../../../factory/custom-validators';
import { containsUrl, wrapUrlsWithAnchor } from "../../../utilities/utilities";

@Component({
  selector: 'app-gsm-manager',
  imports: [
    CommonModule,
    DatePipe,
    ReactiveFormsModule,
    TextBoxComponent,
    InputsModule,
    LabelModule,
    KENDO_CHIP,
    KENDO_GRID,
    KENDO_DROPDOWNLIST,
    KENDO_PAGER,
    KENDO_DATEINPUTS
  ],
  templateUrl: './gsm-manager.component.html',
  styleUrl: './gsm-manager.component.scss'
})
export class GsmManagerComponent {
  // Kendo UI Grid configuration
  dataBinding = viewChild<DataBindingDirective>(DataBindingDirective);
  gridData: GsmFullModelWithStatus[] = [];
  gridView: GsmFullModelWithStatus[] = [];
  pageSize = 10;
  tableColumns: any[] = [
    {
      "field": "status",
      "title": "Status",
      "width": 100
    },
    {
      "field": "message",
      "title": "Message"
    },
    {
      "field": "messageType",
      "title": "Type",
      "width": 100
    },
    {
      "field": "startDatetime",
      "title": "Start Date Time",
      "width": 140
    },
    {
      "field": "endDatetime",
      "title": "End Date Time",
      "width": 140
    }
  ];
  pageSizes = KENDO_GRID_PAGE_SIZES;
  total = 0;
  msgTypeColumnWidth!: number;  // Width of the message type column. This is used to adjust the width of the column when editing
  editColumnWidth!: number;  // Width of the edit column. This is used to adjust the width of the column when editing
  dateColumnWidth!: number;  // Width of the date columns. This is used to adjust the width of the column when editing
  // END Kendo UI Grid configuration

  userId = '';
  formGroup: FormGroup | undefined;
  editedRowIndex: number | undefined;
  messageTypesArray = [
    {
      key: 'info',
      value: 'info',
    },
    {
      key: 'warning',
      value: 'warning',
    },
    {
      key: 'error',
      value: 'error',
    }
  ];
  statusTypes = StatusTypes
  gsmType = GsmType;

  readonly pencilIcon: SVGIcon = pencilIcon;

  #apiService = inject(ApiService);
  #alertService = inject(AlertService);
  #spinnerService = inject(SpinnerService);
  #profileService = inject(ProfileService);
  #notificationService = inject(NotificationService);

  constructor() {
    this.#setColumnWidths();
    this.userId = this.#profileService.userProfile()?.profile.email ?? '';
    this.#fetchGsmData();
  }

  /**
   * Filter the grid data based on the input value
   */
  onFilter(value: Event): void {
    this.gridView = process(this.gridData, {
      filter: {
        logic: "or",
        filters: [
          {
            field: "message",
            operator: "contains",
            value: value,
          },
          {
            field: "messageType",
            operator: "contains",
            value: value,
          },
          {
            field: "status",
            operator: "contains",
            value: value,
          }
        ],
      },
    }).data;

    this.dataBinding()!.skip = 0;
    this.total = this.gridView.length;
  }

  addHandler({ sender }: AddEvent): void {
    this.#closeEditor(sender);
    this.#updateColumnWidths({ edit: 228 });

    this.formGroup = new FormGroup({
      id: new FormControl(''),
      message: new FormControl('', Validators.required),
      messageType: new FormControl(this.gsmType.INFO, Validators.required),
      startDatetime: new FormControl(new Date(), Validators.required),
      endDatetime: new FormControl(new Date(), Validators.required),
    }, { validators: dateRangeValidator('startDatetime', 'endDatetime') });

    sender.addRow(this.formGroup);
  }

  editHandler(args: EditEvent): void {
    // define all editable fields validators and default values
    const { dataItem } = args;
    this.#closeEditor(args.sender);
    this.#updateColumnWidths();

    this.formGroup = new FormGroup({
      id: new FormControl(dataItem.id),
      message: new FormControl(dataItem.message, Validators.required),
      messageType: new FormControl(args.dataItem.messageType, Validators.required),
      startDatetime: new FormControl(new Date(args.dataItem.startDatetime), Validators.required),
      endDatetime: new FormControl(new Date(args.dataItem.endDatetime), Validators.required),
    }, { validators: dateRangeValidator('startDatetime', 'endDatetime') });

    this.editedRowIndex = args.rowIndex;
    // put the row in edit mode, with the `FormGroup` build above
    args.sender.editRow(args.rowIndex, this.formGroup);
  }

  cancelHandler(args: CancelEvent): void {
    // close the editor for the given row
    this.#closeEditor(args.sender, args.rowIndex);
  }

  saveHandler({ sender, rowIndex, formGroup, isNew }: SaveEvent): void {
    // First check if the message contains a URL
    const doesMessageContainsUrl = containsUrl(formGroup?.get('message')!.value);
    let message = formGroup?.get('message')!.value;

    if (doesMessageContainsUrl) {
      // Add code here to wrap the URL in an anchor tag
      message = wrapUrlsWithAnchor(formGroup?.get('message')!.value);
    }

    if (isNew) {
      const requestBody = {
        message: message,
        messageType: formGroup?.get('messageType')!.value,
        startDatetime: formGroup?.get('startDatetime')!.value,
        endDatetime: formGroup?.get('endDatetime')!.value,
        modifiedBy: this.userId,
        createdBy: this.userId
      };
      this.#createMessage(requestBody);
    } else {
      const requestBody = {
        message: message,
        messageType: formGroup?.get('messageType')!.value,
        startDatetime: formGroup?.get('startDatetime')!.value,
        endDatetime: formGroup?.get('endDatetime')!.value,
        id: formGroup?.get('id')!.value,
        modifiedBy: this.userId
      };
      this.#editMessage(requestBody);
    }

    this.#closeEditor(sender, rowIndex);
  }

  getStatus(gsm: GsmFullModel): string {
    const currentDate = new Date();
    const { startDatetime, endDatetime } = gsm;

    if (!startDatetime || !endDatetime) {
      throw new Error('Start and end datetime must be defined');
    }

    if (currentDate < new Date(startDatetime)) {
      return StatusTypes.Upcoming;
    } else if (currentDate >= new Date(startDatetime) && currentDate <= new Date(endDatetime)) {
      return StatusTypes.Current;
    } else {
      return StatusTypes.Expired;
    }
  }

  #closeEditor(grid: GridComponent, rowIndex = this.editedRowIndex) {
    // close the editor
    grid.closeRow(rowIndex);
    // reset the helpers
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
    this.#setColumnWidths();
  }

  #fetchGsmData() {
    this.#spinnerService.show();

    this.#apiService.getAllGeneralSystemMessage().subscribe({
      next: messages => {
        const updatedMessages = messages.map(message => {
          const gsmFullModel = new GsmFullModel(
            message.id,
            message.message,
            message.messageType,
            message.startDatetime ? new Date(message.startDatetime) : undefined,
            message.endDatetime ? new Date(message.endDatetime) : undefined,
            message.modifiedBy,
            message.createdBy,
            message.createdDate,
            message.modifiedDate
          );
          return { ...gsmFullModel, status: this.getStatus(gsmFullModel) };
        });
        this.gridData = updatedMessages;
        this.gridView = updatedMessages;
        this.total = this.gridView.length;
      },
      error: (error: any) => {
        this.#spinnerService.hide();
        this.#alertService.showAlert({
          message: `Failed to get messages.`,
          type: AlertEnum.error,
        });
      },
      complete: () => {
        this.#spinnerService.hide();
      }
    });
  }

  #updateColumnWidths(widths: { msgType?: number; edit?: number; date?: number } = {
    msgType: 200,
    edit: 170,
    date: 250
  }) {
    this.msgTypeColumnWidth = widths.msgType ?? 200;
    this.editColumnWidth = widths.edit ?? 170;
    this.dateColumnWidth = widths.date ?? 250;
  }

  #setColumnWidths() {
    this.msgTypeColumnWidth = 100;
    this.editColumnWidth = 80;
    this.dateColumnWidth = 150;
  }

  #createMessage(requestBody: GsmCreateModel) {
    this.#spinnerService.show();

    this.#apiService.createGeneralSystemMessage(requestBody).subscribe({
      next: message => {
        const gsmFullModel = new GsmFullModel(
          message.id,
          message.message,
          message.messageType,
          message.startDatetime ? new Date(message.startDatetime) : undefined,
          message.endDatetime ? new Date(message.endDatetime) : undefined,
          message.modifiedBy,
          message.createdBy,
          message.createdDate,
          message.modifiedDate
        );
        const updatedMessage = { ...gsmFullModel, status: this.getStatus(gsmFullModel) };
        this.gridData = [updatedMessage, ...this.gridData];
        this.gridView = [ ...this.gridData ];
        this.#updateNotifications(message);
        this.#alertService.showAlert({
          message: `Message created successfully.`,
          type: AlertEnum.success,
        });
      },
      error: (error: any) => {
        this.#alertService.showAlert({
          message: error.error.message,
          type: AlertEnum.error,
        });
        this.#spinnerService.hide();
      },
      complete: () => {
        this.#spinnerService.hide();
      }
    });
  }

  #editMessage(requestBody: GsmFullModel) {
    this.#spinnerService.show();

    this.#apiService.updateGeneralSystemMessage(requestBody).subscribe({
      next: message => {
        // Update gridData and gridView with the values from message returned
        const gsmFullModel = new GsmFullModel(
          message.id,
          message.message,
          message.messageType,
          message.startDatetime ? new Date(message.startDatetime) : undefined,
          message.endDatetime ? new Date(message.endDatetime) : undefined,
          message.modifiedBy,
          message.createdBy,
          message.createdDate,
          message.modifiedDate
        );
        const updatedMessage = { ...gsmFullModel, status: this.getStatus(gsmFullModel) };
        this.gridData = this.gridData.map(item => item.id === message.id ? updatedMessage : item);
        this.gridView = [ ...this.gridData ];
        this.#updateNotifications(message);
        this.#alertService.showAlert({
          message: `Message updated successfully.`,
          type: AlertEnum.success,
        });
      },
      error: (error: any) => {
        this.#alertService.showAlert({
          message: error.error.message,
          type: AlertEnum.error,
        });
        this.#spinnerService.hide();
      },
      complete: () => {
        this.#spinnerService.hide();
      }
    });
  }

  /**
   * Updates the general system messages notification list.
   * This will update the badge count on the bell icon in the header.
   */
  #updateNotifications(message: GsmFullModel) {
    const status = this.getStatus(message);

    // Remove the message from the notification list if it exists
    this.#notificationService.generalSystemMessages.update(currentMessages =>
      currentMessages.filter(currentMessage => currentMessage.id !== message.id)
    );

    if (status === StatusTypes.Current) {
      this.#notificationService.generalSystemMessages.update(currentMessages => {
        const messageExists = currentMessages.some(currentMessage => currentMessage.id === message.id);
        if (!messageExists) {
          return [ ...currentMessages, message ];
        }
        return currentMessages;
      });
    }
  }
}
